/**
 * Just enough category management to use the app before Settings exists.
 *
 * Items can only attach to a subcategory, so a Docket with no tree is a Docket
 * you cannot put anything into. Full management -- rename, recolour, reorder,
 * archive, merge -- belongs on the Settings page in phase 7; what is here is
 * the minimum that stops the first sign-in being a dead end.
 */

import { useState, type FormEvent } from 'react'

import { ApiError, api } from '@/api/client'
import { useToast } from '@/components/Toast'

interface Props {
  onCreated: () => void | Promise<void>
}

/**
 * First run: one form that creates a category and its first subcategory
 * together, because either alone still leaves you unable to add an item.
 */
export function FirstCategoryForm({ onCreated }: Props) {
  const toast = useToast()
  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!category.trim() || !subcategory.trim() || busy) return

    setBusy(true)
    try {
      const created = await api.categories.create(category.trim())
      await api.subcategories.create(created.id, subcategory.trim())
      setCategory('')
      setSubcategory('')
      toast.success(`Created ${created.name} › ${subcategory.trim()}`)
      await onCreated()
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not create that.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mx-auto flex w-full max-w-md flex-col gap-2 text-left">
      <label className="text-xs font-medium text-ink-2" htmlFor="first-category">
        Category
      </label>
      <input
        id="first-category"
        className="field"
        placeholder="Teaching"
        value={category}
        onChange={(event) => setCategory(event.target.value)}
        maxLength={120}
      />

      <label className="mt-1 text-xs font-medium text-ink-2" htmlFor="first-subcategory">
        First subcategory
      </label>
      <input
        id="first-subcategory"
        className="field"
        placeholder="ACC 525"
        value={subcategory}
        onChange={(event) => setSubcategory(event.target.value)}
        maxLength={120}
      />

      <button
        type="submit"
        className="btn btn-primary mt-2"
        disabled={busy || !category.trim() || !subcategory.trim()}
      >
        {busy ? 'Creating...' : 'Create and start adding'}
      </button>
    </form>
  )
}

/** A one-field inline form, used for both "new category" and "new subcategory". */
export function InlineNameForm({
  label,
  placeholder,
  onSubmit,
  onDone,
}: {
  label: string
  placeholder: string
  onSubmit: (name: string) => Promise<unknown>
  onDone: () => void | Promise<void>
}) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!name.trim() || busy) return
    setBusy(true)
    try {
      await onSubmit(name.trim())
      setName('')
      setOpen(false)
      await onDone()
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not create that.')
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="text-xs font-medium text-navy hover:underline"
        onClick={() => setOpen(true)}
      >
        + {label}
      </button>
    )
  }

  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <input
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the field only
        // exists because the user just asked for it; not focusing it means an
        // extra click every time.
        autoFocus
        className="field w-44 px-2 py-1 text-sm"
        placeholder={placeholder}
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false)
        }}
        maxLength={120}
        aria-label={label}
      />
      <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !name.trim()}>
        Add
      </button>
      <button
        type="button"
        className="text-xs text-ink-3 hover:text-ink"
        onClick={() => setOpen(false)}
      >
        Cancel
      </button>
    </form>
  )
}
